import { formatToDate } from '@/helpers/dateHelper';
import { useAppDispatch } from '@/state/hooks';
import { FXSwaps } from '@/types/sso';
import { useCallback, useEffect, useState } from 'react';
import { fetchPortalAPI, useSso } from '../providers/SSOProvider';

export interface UseFXSwapsFilterProps {
    startDate: { label: string; value: Date };
    endDate: { label: string; value: Date };
}

export interface UseFXSwapsFilterReturn {
    data: FXSwaps[];
    isLoading: boolean;
    isValidating: boolean;
    mutate: () => void;
    error: Error | null;
    isResultsFetched: boolean;
    setIsResultsFetched: (value: boolean) => void;
}

export const useFXSwapsFilter = (props: UseFXSwapsFilterProps): UseFXSwapsFilterReturn => {
    const { startDate, endDate } = props;
    const dispatch = useAppDispatch();

    const { jwt, foundEntity, onFetchJWT } = useSso();

    const [fxSwaps, setFXSwaps] = useState<FXSwaps[]>([]);
    const [isResultsFetched, setIsResultsFetched] = useState<boolean>(false);
    const [isFXSwapsLoading, setIsFXSwapsLoading] = useState<boolean>(false);
    const [isFXSwapsValidating, setIsFXSwapsValidating] = useState<boolean>(false);
    const [fxSwapsError, setFXSwapsError] = useState<Error | null>(null);

    const handleApiError = useCallback(
        (error) => {
            setIsFXSwapsLoading(false);
            setIsFXSwapsValidating(false);
        },
        [setIsFXSwapsLoading, setIsFXSwapsLoading]
    );

    const refreshAllFXSwaps = useCallback(async () => {
        setIsFXSwapsLoading(true);
        setIsFXSwapsValidating(true);
        setIsResultsFetched(true);

        try {
            if (foundEntity) {
                const swaps = await fetchPortalAPI<FXSwaps[]>(
                    onFetchJWT,
                    '/api/fx-swap-rates',
                    jwt,
                    {
                        id: foundEntity.entityId.toString(),
                        fromTradeDate: formatToDate(startDate.value),
                        toTradeDate: formatToDate(endDate.value)
                    },
                    'POST'
                );
                setIsFXSwapsLoading(false);
                setIsFXSwapsValidating(false);
                setIsResultsFetched(true);
                setFXSwaps(swaps || []);
            }
        } catch (error: any) {
            setFXSwapsError(error);
            handleApiError({
                title: error?.name || 'Unknown Error',
                message: error?.message || 'An unknown error occurred'
            });
        }
    }, [
        startDate,
        endDate,
        foundEntity,
        isResultsFetched,
        dispatch,
        handleApiError,
        setIsFXSwapsLoading,
        setIsFXSwapsValidating,
        setFXSwaps
    ]);

    useEffect(() => {
        setFXSwaps([]);
        setIsResultsFetched(false);
    }, [foundEntity]);

    return {
        data: fxSwaps,
        isLoading: isFXSwapsLoading,
        isValidating: isFXSwapsValidating,
        mutate: refreshAllFXSwaps,
        error: fxSwapsError,
        isResultsFetched,
        setIsResultsFetched
    };
};
